import { useState, useEffect, useCallback } from 'react';

// The theme itself is applied to <html data-theme> by the inline script in gatsby-ssr.js
// before first paint, and every colour is driven by CSS from that attribute. This hook
// only mirrors it into React state (for the toggle's label) and handles changes.
// The state starts as false on both server and client so hydration always matches.
export function useDarkMode() {
  const [darkMode, setDarkModeState] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    setDarkModeState(root.getAttribute('data-theme') === 'dark');

    // Follow the OS setting until the visitor picks a theme themselves.
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => {
      let saved = null;
      try {
        saved = window.localStorage.getItem('darkMode');
      } catch (e) {
        // ignore unreadable or blocked storage
      }
      if (saved !== null) return;
      root.setAttribute('data-theme', event.matches ? 'dark' : 'light');
      setDarkModeState(event.matches);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const setDarkMode = useCallback((value) => {
    const root = document.documentElement;
    // Fade colours only for a deliberate toggle, never on page load.
    root.classList.add('theme-transition');
    root.setAttribute('data-theme', value ? 'dark' : 'light');
    window.setTimeout(() => root.classList.remove('theme-transition'), 350);
    try {
      window.localStorage.setItem('darkMode', JSON.stringify(value));
    } catch (e) {
      // ignore unreadable or blocked storage
    }
    setDarkModeState(value);
  }, []);

  return [darkMode, setDarkMode];
}
